import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  MetricCard,
  PORTAL_PAGE_TITLE_ID,
} from '@beautyburst/ui';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import type {
  AdminMembershipPlan,
  AdminMembershipPlanVersion,
  AcquisitionSource,
  AdminApiClient,
  AdminLead,
  AdminMarket,
  AdminMembership,
  AdminMembershipBillingAttempt,
  MembershipBenefitRuleCreateInput,
  MembershipPlanCreateInput,
  AdminRecord,
  MarketStatus,
  SearchTermInsight,
} from '../api-client.ts';
import { GENERIC_ADMIN_AUTH_FAILURE } from '../api-client.ts';
import { AdminApiError } from '../http-client.ts';
import type { RouteContract } from '../routes.ts';

type Remote<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly value: T }
  | { readonly status: 'failed'; readonly error: unknown };

function field(row: AdminRecord, key: string): string {
  const value = row[key];
  return value === null || value === undefined ? '—' : String(value);
}

// Operational tables show real recorded times. A missing timestamp renders as an
// em dash rather than an empty cell so the founder can tell "not yet" from "no data".
function timestampText(value: unknown): string {
  if (typeof value !== 'string' || value === '') return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function money(minor: unknown, currency: unknown): string {
  if (
    typeof minor !== 'number' ||
    !Number.isInteger(minor) ||
    typeof currency !== 'string' ||
    !/^[A-Z]{3}$/u.test(currency)
  )
    return '—';
  try {
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency });
    const fractionDigits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
    return formatter.format(minor / 10 ** fractionDigits);
  } catch {
    return '—';
  }
}

function errorDescription(error: unknown): string {
  // The API's user-facing message distinguishes an ordinary failed request
  // without leaking its request id, internal code, details, or stack trace.
  if (error instanceof AdminApiError) return error.message;
  return 'The administrator service did not return data. Try again.';
}

function isMfaError(error: unknown): boolean {
  return error instanceof AdminApiError && error.code === 'PRECONDITION_FAILED';
}

function actionFailure(error: unknown): ReactNode {
  if (isMfaError(error)) {
    return (
      <ErrorState
        title="Multi-factor authentication required"
        description="Complete MFA for this session, then retry this administrator action."
        headingLevel={2}
      />
    );
  }
  return (
    <ErrorState
      title="Administrator action unavailable"
      description={errorDescription(error)}
      headingLevel={2}
    />
  );
}

function optionalNumber(value: FormDataEntryValue | null): number | undefined {
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

const MARKET_STATUSES: readonly MarketStatus[] = [
  'PLANNED',
  'WAITLIST',
  'LIVE',
  'PAUSED',
  'RETIRED',
];

const LEAD_STATUSES = ['NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED', 'NURTURE', 'CLOSED'] as const;
type LeadStatus = (typeof LEAD_STATUSES)[number];
type LeadKind = 'CUSTOMER' | 'RETAILER' | 'MEMBERSHIP';
type LeadRow = AdminLead & { readonly kind: LeadKind };
const ADMIN_PAGE_SIZE = 25;

function isLeadStatus(value: string): value is LeadStatus {
  return LEAD_STATUSES.includes(value as LeadStatus);
}

function isMarketStatus(value: string): value is MarketStatus {
  return MARKET_STATUSES.includes(value as MarketStatus);
}

function useData<T>(load: () => Promise<T>, key: string): [Remote<T>, () => void] {
  const [refresh, setRefresh] = useState(0);
  const [state, setState] = useState<Remote<T>>({ status: 'loading' });
  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    void load().then(
      (value) => active && setState({ status: 'loaded', value }),
      (error: unknown) => active && setState({ status: 'failed', error }),
    );
    return () => {
      active = false;
    };
    // The client is created once at the application boundary. `key` deliberately
    // identifies the endpoint, avoiding a fresh inline loader causing a reload loop.
  }, [key, refresh]);
  return [state, () => setRefresh((value) => value + 1)];
}

function Panel({
  title,
  state,
  onRetry,
  emptyTitle,
  emptyDescription,
  children,
}: {
  readonly title: string;
  readonly state: Remote<unknown>;
  readonly onRetry: () => void;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
  readonly children: ReactNode;
}) {
  if (state.status === 'loading') return <LoadingState label={`Loading ${title}`} />;
  if (state.status === 'failed') {
    if (isMfaError(state.error)) {
      return (
        <ErrorState
          title="Multi-factor authentication required"
          description="This administrator action requires an MFA-verified session. Complete MFA, then try again."
          headingLevel={2}
          retry={<Button onClick={onRetry}>Try again</Button>}
        />
      );
    }
    return (
      <ErrorState
        title={`${title} unavailable`}
        description={errorDescription(state.error)}
        headingLevel={2}
        retry={<Button onClick={onRetry}>Retry request</Button>}
      />
    );
  }
  if (emptyTitle !== undefined)
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription ?? 'Nothing has been returned.'}
        headingLevel={2}
      />
    );
  return <>{children}</>;
}

/**
 * This is intentionally the only screen that can render before an administrator
 * has a permission. The shared auth endpoint keeps customer, retailer, and
 * administrator credential handling consistent without exposing account existence.
 */
export function AdminSignInScreen({
  client,
  onSignedIn,
}: {
  readonly client: Pick<AdminApiClient, 'auth'>;
  readonly onSignedIn: () => void;
}) {
  const [failure, setFailure] = useState<
    | { readonly kind: 'invalid_credentials'; readonly message: string }
    | { readonly kind: 'mfa_required'; readonly message: string }
    | null
  >(null);
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get('email') ?? '').trim();
    const password = String(form.get('password') ?? '');
    if (email === '' || password === '') return;
    setPending(true);
    setFailure(null);
    try {
      const outcome = await client.auth.signIn({ email, password });
      if (outcome.ok) {
        onSignedIn();
        return;
      }
      setFailure({
        kind: outcome.kind,
        message: outcome.kind === 'mfa_required' ? outcome.message : GENERIC_ADMIN_AUTH_FAILURE,
      });
    } catch {
      // A client-side transport failure is deliberately presented with the same
      // generic credential failure as an authentication refusal.
      setFailure({ kind: 'invalid_credentials', message: GENERIC_ADMIN_AUTH_FAILURE });
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="admin-sign-in" aria-labelledby="administrator-sign-in-heading">
      <section>
        <h1 id="administrator-sign-in-heading">Administrator sign in</h1>
        <p>Use the account assigned to administer Beauty Burst.</p>
        <Card>
          <h2>Sign-in credentials</h2>
          {failure === null ? null : (
            <ErrorState
              title={
                failure.kind === 'mfa_required'
                  ? 'Multi-factor authentication required'
                  : 'Unable to sign in'
              }
              description={failure.message}
            />
          )}
          <form className="admin-inline-form" onSubmit={(event) => void submit(event)}>
            <Field
              id="administrator-email"
              name="email"
              type="email"
              label="Email address"
              autoComplete="email"
              required
            />
            <Field
              id="administrator-password"
              name="password"
              type="password"
              label="Password"
              autoComplete="current-password"
              required
            />
            <Button
              type="submit"
              disabled={pending}
              aria-label={pending ? 'Signing in' : 'Sign in to administrator console'}
              style={{ minWidth: '44px', minHeight: '44px' }}
            >
              {pending ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </Card>
      </section>
    </main>
  );
}

function Overview({ client }: { readonly client: AdminApiClient }) {
  const load = () => client.overview.get();
  const [state, retry] = useData(load, 'overview');
  return (
    <section aria-labelledby="overview-heading">
      <h2 id="overview-heading">Platform operations</h2>
      <Panel title="Platform overview" state={state} onRetry={retry}>
        {state.status === 'loaded' ? (
          <div className="admin-metric-grid">
            <MetricCard label="Orders" value={field(state.value, 'order_count')} />
            <MetricCard
              label="Gross merchandise value (minor units)"
              value={field(state.value, 'gmv_minor')}
            />
            <MetricCard label="Approved retailers" value={field(state.value, 'active_retailers')} />
            <MetricCard label="Refunded orders" value={field(state.value, 'refunded_orders')} />
            <MetricCard
              label="Draft payout obligations (minor units)"
              value={field(state.value, 'draft_payout_obligations_minor')}
            />
            <MetricCard
              label="Pending fulfilments"
              value={field(state.value, 'fulfilments_pending')}
            />
            <MetricCard
              label="Delivered fulfilments"
              value={field(state.value, 'fulfilments_delivered')}
            />
          </div>
        ) : null}
      </Panel>
    </section>
  );
}

function Retailers({ client }: { readonly client: AdminApiClient }) {
  const load = () => client.retailers.applications();
  const [state, retry] = useData(load, 'retailers');
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const approve = async (retailerId: string) => {
    setActionError(null);
    try {
      await client.retailers.approve(retailerId);
      retry();
    } catch (error) {
      setActionError(errorDescription(error));
    }
  };
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <p>
        Approval is an explicit administrator action; submitted applications are not approved
        automatically.
      </p>
      {actionError === null ? null : (
        <ErrorState title="Retailer action unavailable" description={actionError} />
      )}
      <Panel
        title="Retailer applications"
        state={state}
        onRetry={retry}
        emptyTitle={
          state.status === 'loaded' && state.value.length === 0
            ? 'No retailer applications'
            : undefined
        }
        emptyDescription="New retailer applications will appear here for explicit review."
      >
        {state.status === 'loaded' ? (
          <DataTable
            caption="Retailer applications awaiting administrator review"
            rows={state.value}
            rowKey={(row) => field(row, 'id')}
            columns={[
              {
                key: 'business',
                header: 'Business',
                render: (row) => field(row, 'business_display_name'),
              },
              {
                key: 'status',
                header: 'Application status',
                render: (row) => field(row, 'status'),
              },
              {
                key: 'action',
                header: 'Explicit action',
                render: (row) => (
                  <Button
                    aria-label={`Explicitly approve ${field(row, 'business_display_name')}`}
                    style={{ minWidth: '44px' }}
                    onClick={() => void approve(field(row, 'retailer_id'))}
                  >
                    Approve
                  </Button>
                ),
              },
            ]}
          />
        ) : null}
      </Panel>
      <form
        className="admin-inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const applicationId = String(form.get('applicationId') ?? '');
          const fields = String(form.get('fields') ?? '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);
          if (applicationId === '' || fields.length === 0 || reason.trim() === '') return;
          void client.retailers
            .requestInfo(applicationId, reason, fields)
            .then(retry, (error: unknown) => setActionError(errorDescription(error)));
        }}
      >
        <h2>Request application information</h2>
        <Field id="retailer-application-id" name="applicationId" label="Application ID" required />
        <Field
          id="retailer-fields"
          name="fields"
          label="Requested fields"
          hint="Separate field names with commas."
          required
        />
        <Field
          id="retailer-reason"
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          required
        />
        <Button type="submit" style={{ minWidth: '44px' }}>
          Request information
        </Button>
      </form>
    </section>
  );
}

function Users({ client }: { readonly client: AdminApiClient }) {
  const [offset, setOffset] = useState(0);
  const load = () => client.users.list({ limit: ADMIN_PAGE_SIZE, offset });
  const [state, retry] = useData(load, `users:${offset}`);
  const [filter, setFilter] = useState('');
  const [actionError, setActionError] = useState<unknown>(null);
  const [detail, setDetail] = useState<{
    readonly user: AdminRecord;
    readonly roles: readonly AdminRecord[];
  } | null>(null);
  const [detailError, setDetailError] = useState<unknown>(null);
  const [revokeTarget, setRevokeTarget] = useState<{
    readonly userId: string;
    readonly roleKey: string;
  } | null>(null);
  const inspect = (userId: string) => {
    setDetailError(null);
    void client.users.detail(userId).then(setDetail, setDetailError);
  };
  const revoke = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (revokeTarget === null) return;
    const reason = String(new FormData(event.currentTarget).get('reason') ?? '').trim();
    if (reason === '') return;
    setActionError(null);
    void client.users.revokeRole(revokeTarget.userId, revokeTarget.roleKey, reason).then(() => {
      retry();
      inspect(revokeTarget.userId);
      setRevokeTarget(null);
    }, setActionError);
  };
  const rows =
    state.status === 'loaded'
      ? state.value.users.filter((user) => {
          const needle = filter.trim().toLowerCase();
          return (
            needle === '' ||
            field(user, 'full_name').toLowerCase().includes(needle) ||
            field(user, 'email').toLowerCase().includes(needle) ||
            field(user, 'status').toLowerCase().includes(needle) ||
            (Array.isArray(user.roles) &&
              user.roles.some((role) => String(role).toLowerCase().includes(needle)))
          );
        })
      : [];
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <Field
        id="user-filter"
        label="Filter name, email, status, or role"
        value={filter}
        onChange={(event) => setFilter(event.currentTarget.value)}
      />
      <Panel
        title="Users"
        state={state}
        onRetry={retry}
        emptyTitle={
          state.status === 'loaded' && rows.length === 0
            ? 'No user records match this filter'
            : undefined
        }
        emptyDescription="No operator-needed user records were returned."
      >
        {state.status === 'loaded' ? (
          <>
            <DataTable
              caption="User account records"
              rows={rows}
              rowKey={(row) => field(row, 'id')}
              columns={[
                { key: 'name', header: 'Name', render: (row) => field(row, 'full_name') },
                { key: 'email', header: 'Email', render: (row) => field(row, 'email') },
                { key: 'status', header: 'Status', render: (row) => field(row, 'status') },
                {
                  key: 'roles',
                  header: 'Active roles',
                  render: (row) => (Array.isArray(row.roles) ? row.roles.join(', ') : '—'),
                },
                {
                  key: 'detail',
                  header: 'Detail',
                  render: (row) => (
                    <Button
                      aria-label={`Inspect user ${field(row, 'email')}`}
                      style={{ minWidth: '44px', minHeight: '44px' }}
                      onClick={() => inspect(field(row, 'id'))}
                    >
                      Inspect
                    </Button>
                  ),
                },
              ]}
            />
            <nav className="admin-pagination" aria-label="User pagination">
              <Button
                variant="secondary"
                disabled={offset === 0}
                style={{ minWidth: '44px', minHeight: '44px' }}
                onClick={() => setOffset((value) => Math.max(0, value - ADMIN_PAGE_SIZE))}
              >
                Previous page
              </Button>
              <span aria-live="polite">
                Showing users {offset + 1}–{offset + rows.length}
              </span>
              <Button
                variant="secondary"
                disabled={state.value.users.length < ADMIN_PAGE_SIZE}
                style={{ minWidth: '44px', minHeight: '44px' }}
                onClick={() => setOffset((value) => value + ADMIN_PAGE_SIZE)}
              >
                Next page
              </Button>
            </nav>
          </>
        ) : null}
      </Panel>
      {actionError === null ? null : actionFailure(actionError)}
      {detailError === null ? null : actionFailure(detailError)}
      {detail === null ? null : (
        <section aria-labelledby="user-detail-heading">
          <h2 id="user-detail-heading">User detail</h2>
          <dl className="admin-definition-list">
            <dt>Name</dt>
            <dd>{field(detail.user, 'full_name')}</dd>
            <dt>Email</dt>
            <dd>{field(detail.user, 'email')}</dd>
            <dt>Status</dt>
            <dd>{field(detail.user, 'status')}</dd>
          </dl>
          <DataTable
            caption="Active roles for inspected user"
            rows={detail.roles}
            rowKey={(role) => field(role, 'id')}
            emptyMessage="This user has no active roles."
            columns={[
              { key: 'role', header: 'Role', render: (role) => field(role, 'role_key') },
              {
                key: 'retailer',
                header: 'Retailer scope',
                render: (role) => field(role, 'retailer_id'),
              },
              {
                key: 'granted',
                header: 'Granted',
                render: (role) => field(role, 'granted_at'),
              },
              {
                key: 'revoke',
                header: 'Action',
                render: (role) => (
                  <Button
                    variant="danger"
                    aria-label={`Revoke role ${field(role, 'role_key')}`}
                    style={{ minWidth: '44px', minHeight: '44px' }}
                    onClick={() =>
                      setRevokeTarget({
                        userId: field(detail.user, 'id'),
                        roleKey: field(role, 'role_key'),
                      })
                    }
                  >
                    Revoke
                  </Button>
                ),
              },
            ]}
          />
          {revokeTarget === null ? null : (
            <form className="admin-inline-form" onSubmit={revoke}>
              <h3>Revoke {revokeTarget.roleKey}</h3>
              <Field id="role-revoke-reason" name="reason" label="Reason for revocation" required />
              <Button
                type="submit"
                variant="danger"
                style={{ minWidth: '44px', minHeight: '44px' }}
              >
                Confirm role revocation
              </Button>
            </form>
          )}
        </section>
      )}
      <form
        className="admin-inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const userId = String(form.get('userId') ?? '').trim();
          const roleKey = String(form.get('roleKey') ?? '').trim();
          const reason = String(form.get('reason') ?? '').trim();
          const retailerId = String(form.get('retailerId') ?? '').trim();
          if (userId === '' || roleKey === '' || reason === '') return;
          setActionError(null);
          void client.users
            .grantRole(userId, { roleKey, reason, ...(retailerId === '' ? {} : { retailerId }) })
            .then(retry, setActionError);
        }}
      >
        <h2>Grant a role</h2>
        <Field id="role-user-id" name="userId" label="User ID" required />
        <Field id="role-key" name="roleKey" label="Role key" required />
        <Field id="role-retailer-id" name="retailerId" label="Retailer ID (for retailer roles)" />
        <Field id="role-reason" name="reason" label="Reason" required />
        <Button type="submit" style={{ minWidth: '44px' }}>
          Grant role
        </Button>
      </form>
    </section>
  );
}

function FeatureFlags({ client }: { readonly client: AdminApiClient }) {
  const load = () => client.flags.list();
  const [state, retry] = useData(load, 'flags');
  const [actionError, setActionError] = useState<unknown>(null);
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <Panel
        title="Feature flags"
        state={state}
        onRetry={retry}
        emptyTitle={
          state.status === 'loaded' && state.value.length === 0 ? 'No feature flags' : undefined
        }
        emptyDescription="The administrator API returned no registered flags."
      >
        {state.status === 'loaded' ? (
          <DataTable
            caption="Registered feature flags"
            rows={state.value}
            rowKey={(row) => field(row, 'key')}
            columns={[
              { key: 'key', header: 'Flag', render: (row) => field(row, 'key') },
              {
                key: 'description',
                header: 'Description',
                render: (row) => field(row, 'description'),
              },
              { key: 'enabled', header: 'State', render: (row) => field(row, 'enabled') },
              {
                key: 'approval',
                header: 'Founder approval',
                render: (row) => field(row, 'requiresFounderApproval'),
              },
            ]}
          />
        ) : null}
      </Panel>
      {actionError === null ? null : actionFailure(actionError)}
      <form
        className="admin-inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const key = String(form.get('key') ?? '').trim();
          const reason = String(form.get('reason') ?? '').trim();
          const scopeKind = String(form.get('scopeKind') ?? '');
          const scopeId = String(form.get('scopeId') ?? '').trim();
          const rolloutPercentage = optionalNumber(form.get('rolloutPercentage'));
          if (
            key === '' ||
            reason === '' ||
            !['global', 'market', 'retailer', 'location', 'member_group'].includes(scopeKind)
          )
            return;
          setActionError(null);
          void client.flags
            .update(key, {
              enabled: form.get('enabled') === 'on',
              scopeKind: scopeKind as
                'global' | 'market' | 'retailer' | 'location' | 'member_group',
              ...(scopeId === '' ? {} : { scopeId }),
              ...(rolloutPercentage === undefined ? {} : { rolloutPercentage }),
              reason,
              founderApproved: form.get('founderApproved') === 'on',
            })
            .then(retry, setActionError);
        }}
      >
        <h2>Change a feature flag</h2>
        <Field id="flag-key" name="key" label="Flag key" required />
        <label>
          Scope
          <select name="scopeKind" defaultValue="global">
            <option value="global">Global</option>
            <option value="market">Market</option>
            <option value="retailer">Retailer</option>
            <option value="location">Location</option>
            <option value="member_group">Member group</option>
          </select>
        </label>
        <Field id="flag-scope-id" name="scopeId" label="Scope ID" />
        <Field
          id="flag-rollout-percentage"
          name="rolloutPercentage"
          label="Rollout percentage"
          inputMode="numeric"
        />
        <label>
          <input name="enabled" type="checkbox" /> Enable this flag
        </label>
        <label>
          <input name="founderApproved" type="checkbox" /> Founder approval recorded
        </label>
        <Field id="flag-reason" name="reason" label="Reason" required />
        <Button type="submit" style={{ minWidth: '44px' }}>
          Save flag
        </Button>
      </form>
    </section>
  );
}

function Audit({ client }: { readonly client: AdminApiClient }) {
  const load = () => client.audit.list();
  const [state, retry] = useData(load, 'audit');
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <p>This append-only record has no edit or delete actions.</p>
      <Panel
        title="Audit log"
        state={state}
        onRetry={retry}
        emptyTitle={
          state.status === 'loaded' && state.value.entries.length === 0
            ? 'No audit entries'
            : undefined
        }
        emptyDescription="No audit events have been returned."
      >
        {state.status === 'loaded' ? (
          <DataTable
            caption="Append-only audit events"
            rows={state.value.entries}
            rowKey={(row) => field(row, 'id')}
            columns={[
              { key: 'action', header: 'Action', render: (row) => field(row, 'action') },
              { key: 'outcome', header: 'Outcome', render: (row) => field(row, 'outcome') },
              { key: 'time', header: 'Occurred', render: (row) => field(row, 'occurred_at') },
            ]}
          />
        ) : null}
      </Panel>
    </section>
  );
}

function Orders({ client }: { readonly client: AdminApiClient }) {
  const [offset, setOffset] = useState(0);
  const load = () => client.orders.list({ limit: ADMIN_PAGE_SIZE, offset });
  const [state, retry] = useData(load, `orders:${offset}`);
  const [filter, setFilter] = useState('');
  const [detail, setDetail] = useState<{
    readonly order: AdminRecord;
    readonly lines: readonly AdminRecord[];
  } | null>(null);
  const [detailError, setDetailError] = useState<unknown>(null);
  const [exceptionError, setExceptionError] = useState<unknown>(null);
  const rows =
    state.status === 'loaded'
      ? state.value.orders.filter((row) => {
          const needle = filter.trim().toLowerCase();
          return (
            needle === '' ||
            field(row, 'id').toLowerCase().includes(needle) ||
            field(row, 'fulfilment_statuses').toLowerCase().includes(needle)
          );
        })
      : [];
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <Field
        id="order-filter"
        label="Filter order ID or fulfilment status"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
      />
      <Panel
        title="Orders"
        state={state}
        onRetry={retry}
        emptyTitle={state.status === 'loaded' && rows.length === 0 ? 'No orders' : undefined}
        emptyDescription="No orders have been returned for oversight."
      >
        {state.status === 'loaded' ? (
          <>
            <DataTable
              caption="Orders"
              rows={rows}
              rowKey={(row) => field(row, 'id')}
              columns={[
                { key: 'id', header: 'Order', render: (row) => field(row, 'id') },
                {
                  key: 'total',
                  header: 'Total',
                  numeric: true,
                  render: (row) => money(row.total_minor, row.currency),
                },
                { key: 'sellers', header: 'Sellers', render: (row) => field(row, 'seller_count') },
                {
                  key: 'detail',
                  header: 'Detail',
                  render: (row) => (
                    <Button
                      aria-label={`Read order ${field(row, 'id')}`}
                      style={{ minWidth: '44px' }}
                      onClick={() => {
                        setDetailError(null);
                        void client.orders.detail(field(row, 'id')).then(setDetail, setDetailError);
                      }}
                    >
                      View
                    </Button>
                  ),
                },
              ]}
            />
            <nav className="admin-pagination" aria-label="Order pagination">
              <Button
                variant="secondary"
                disabled={offset === 0}
                style={{ minWidth: '44px', minHeight: '44px' }}
                onClick={() => setOffset((value) => Math.max(0, value - ADMIN_PAGE_SIZE))}
              >
                Previous page
              </Button>
              <span aria-live="polite">
                Showing orders {offset + 1}–{offset + rows.length}
              </span>
              <Button
                variant="secondary"
                disabled={state.value.orders.length < ADMIN_PAGE_SIZE}
                style={{ minWidth: '44px', minHeight: '44px' }}
                onClick={() => setOffset((value) => value + ADMIN_PAGE_SIZE)}
              >
                Next page
              </Button>
            </nav>
          </>
        ) : null}
      </Panel>
      {detailError === null ? null : actionFailure(detailError)}
      {exceptionError === null ? null : actionFailure(exceptionError)}
      {detail === null ? null : (
        <section aria-labelledby="order-lines-heading">
          <h2 id="order-lines-heading">Order lines</h2>
          <DataTable
            caption="Server-recorded order lines"
            rows={detail.lines}
            rowKey={(row) => field(row, 'id')}
            columns={[
              {
                key: 'seller',
                header: 'Seller',
                render: (row) => `Sold by ${field(row, 'seller_display_name')}`,
              },
              {
                key: 'quantity',
                header: 'Quantity',
                numeric: true,
                render: (row) => field(row, 'quantity'),
              },
              {
                key: 'total',
                header: 'Line total',
                numeric: true,
                render: (row) => money(row.line_total_minor, row.currency),
              },
            ]}
          />
          <form
            className="admin-inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              const reason = String(form.get('reason') ?? '');
              const note = String(form.get('note') ?? '').trim();
              const accepted = [
                'DAMAGED',
                'INCORRECT_ITEM',
                'MISSING_ITEM',
                'NOT_DELIVERED',
                'UNDELIVERABLE',
                'CANCELLED',
                'FAILED',
                'DUPLICATE',
                'DEFECTIVE',
              ];
              if (!accepted.includes(reason) || typeof detail.order.id !== 'string') return;
              setExceptionError(null);
              void client.orders
                .createException(detail.order.id, {
                  reason: reason as
                    | 'DAMAGED'
                    | 'INCORRECT_ITEM'
                    | 'MISSING_ITEM'
                    | 'NOT_DELIVERED'
                    | 'UNDELIVERABLE'
                    | 'CANCELLED'
                    | 'FAILED'
                    | 'DUPLICATE'
                    | 'DEFECTIVE',
                  ...(note === '' ? {} : { note }),
                })
                .then(() => {
                  event.currentTarget.reset();
                  retry();
                }, setExceptionError);
            }}
          >
            <h3>Record an operational exception</h3>
            <p>
              This is an internal administrator action. It does not create a customer return or
              refund request.
            </p>
            <label htmlFor="order-exception-reason">
              Exception reason
              <select id="order-exception-reason" name="reason" defaultValue="DAMAGED">
                <option value="DAMAGED">Damaged</option>
                <option value="INCORRECT_ITEM">Incorrect item</option>
                <option value="MISSING_ITEM">Missing item</option>
                <option value="NOT_DELIVERED">Not delivered</option>
                <option value="UNDELIVERABLE">Undeliverable</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="FAILED">Failed</option>
                <option value="DUPLICATE">Duplicate</option>
                <option value="DEFECTIVE">Defective</option>
              </select>
            </label>
            <Field id="order-exception-note" name="note" label="Operational note" />
            <Button type="submit">Record exception</Button>
          </form>
        </section>
      )}
    </section>
  );
}

function OperationalExceptions({ client }: { readonly client: AdminApiClient }) {
  const load = async () => {
    if (client.exceptions !== undefined) return client.exceptions.list();
    // The HTTP exception client is assembled outside this launch-owned screen. Preserve
    // the existing read failure/MFA state until that client is wired, while deliberately
    // discarding legacy return records so they never become a return-review interface.
    await client.refunds.list();
    return { exceptions: [] as readonly AdminRecord[] };
  };
  const [state, retry] = useData(load, 'operational exceptions');
  const [selected, setSelected] = useState<AdminRecord | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const resolve = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selected === null || client.exceptions === undefined) return;
    const form = new FormData(event.currentTarget);
    const outcome = String(form.get('outcome') ?? '');
    const note = String(form.get('note') ?? '').trim();
    const orderLineId = String(form.get('orderLineId') ?? '').trim();
    const amountMinor = optionalNumber(form.get('amountMinor'));
    if (!['REDELIVER', 'CANCEL', 'RETURN_FUNDS'].includes(outcome)) return;
    if (outcome === 'RETURN_FUNDS' && (orderLineId === '' || amountMinor === undefined)) return;
    setActionError(null);
    void client.exceptions
      .resolve(field(selected, 'id'), {
        outcome: outcome as 'REDELIVER' | 'CANCEL' | 'RETURN_FUNDS',
        ...(outcome === 'RETURN_FUNDS' ? { orderLineId, amountMinor } : {}),
        ...(note === '' ? {} : { note }),
      })
      .then(() => {
        setSelected(null);
        retry();
      }, setActionError);
  };
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <p>
        Resolve damaged, incorrect, missing, undeliverable, cancelled, failed, duplicate, or
        defective orders. This is administrator-operated order oversight, not a customer-initiated
        workflow.
      </p>
      <Panel
        title="Order exceptions"
        state={state}
        onRetry={retry}
        emptyTitle={
          state.status === 'loaded' && state.value.exceptions.length === 0
            ? 'No operational exceptions'
            : undefined
        }
        emptyDescription="No damaged, incorrect, missing, undeliverable, cancelled, failed, duplicate, or defective orders need resolution."
      >
        {state.status === 'loaded' ? (
          <DataTable
            caption="Operational order exceptions"
            rows={state.value.exceptions}
            rowKey={(row) => field(row, 'id')}
            columns={[
              { key: 'order', header: 'Order', render: (row) => field(row, 'order_id') },
              {
                key: 'seller',
                header: 'Seller',
                render: (row) => `Sold by ${field(row, 'seller_display_name')}`,
              },
              { key: 'reason', header: 'Reason', render: (row) => field(row, 'reason') },
              // Recorded/recorded-by describe the exception itself, so they are
              // populated the moment it is raised. The amount, resolver, and
              // resolved time all belong to a resolution and therefore stay an em
              // dash until one exists — previously every one of these columns read
              // from a resolution-only field, so a freshly recorded exception showed
              // three blank cells and no indication of when or by whom it was raised.
              {
                key: 'recorded',
                header: 'Recorded',
                render: (row) => timestampText(row.created_at),
              },
              {
                key: 'recorded-by',
                header: 'Recorded by',
                render: (row) => field(row, 'created_by_user_id'),
              },
              {
                key: 'amount',
                header: 'Affected amount',
                numeric: true,
                render: (row) => money(row.amount_minor, row.currency),
              },
              {
                key: 'resolver',
                header: 'Resolver',
                render: (row) => field(row, 'resolved_by_user_id'),
              },
              {
                key: 'resolved-at',
                header: 'Resolved',
                render: (row) => timestampText(row.completed_at),
              },
              {
                key: 'resolve',
                header: 'Resolution',
                render: (row) =>
                  field(row, 'resolved_at') === '—' ? (
                    <Button
                      aria-label={`Resolve operational exception ${field(row, 'id')}`}
                      style={{ minWidth: '44px', minHeight: '44px' }}
                      onClick={() => setSelected(row)}
                    >
                      Resolve
                    </Button>
                  ) : (
                    'Resolved'
                  ),
              },
            ]}
          />
        ) : null}
      </Panel>
      {actionError === null ? null : actionFailure(actionError)}
      {selected === null ? null : (
        <form className="admin-inline-form" onSubmit={resolve}>
          <h2>Resolve operational exception {field(selected, 'id')}</h2>
          <p>
            This is founder-authorized operational handling only. It is not a customer-facing refund
            initiation flow.
          </p>
          <label htmlFor="exception-outcome">
            Operational outcome
            <select id="exception-outcome" name="outcome" defaultValue="REDELIVER">
              <option value="REDELIVER">Redeliver</option>
              <option value="CANCEL">Cancel</option>
              <option value="RETURN_FUNDS">Return funds for a specified line</option>
            </select>
          </label>
          <Field id="exception-order-line" name="orderLineId" label="Order line ID (funds only)" />
          <Field
            id="exception-amount-minor"
            name="amountMinor"
            label="Amount (minor units; funds only)"
            inputMode="numeric"
          />
          <Field id="exception-note" name="note" label="Resolution note" />
          <Button type="submit" style={{ minWidth: '44px', minHeight: '44px' }}>
            Complete operational resolution
          </Button>
        </form>
      )}
    </section>
  );
}

function Payouts({ client }: { readonly client: AdminApiClient }) {
  const load = () => client.payouts.list();
  const [state, retry] = useData(load, 'payouts');
  const [actionError, setActionError] = useState<unknown>(null);
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <p>Settlement evidence is append-only; ledger entries cannot be edited or deleted here.</p>
      <Panel
        title="Payout batches"
        state={state}
        onRetry={retry}
        emptyTitle={
          state.status === 'loaded' && state.value.length === 0 ? 'No payout batches' : undefined
        }
        emptyDescription="No payout batches have been returned."
      >
        {state.status === 'loaded' ? (
          <DataTable
            caption="Payout batches"
            rows={state.value}
            rowKey={(row) => field(row, 'id')}
            columns={[
              { key: 'id', header: 'Batch', render: (row) => field(row, 'id') },
              { key: 'status', header: 'Status', render: (row) => field(row, 'status') },
              {
                key: 'payout',
                header: 'Server-recorded payout',
                numeric: true,
                render: (row) => money(row.payout_minor, row.currency),
              },
              {
                key: 'finalise',
                header: 'Action',
                render: (row) => (
                  <Button
                    aria-label={`Finalise payout batch ${field(row, 'id')}`}
                    style={{ minWidth: '44px' }}
                    onClick={() => {
                      setActionError(null);
                      void client.payouts.finalise(field(row, 'id')).then(retry, setActionError);
                    }}
                  >
                    Finalise
                  </Button>
                ),
              },
            ]}
          />
        ) : null}
      </Panel>
      {actionError === null ? null : actionFailure(actionError)}
    </section>
  );
}

function Commissions({ client }: { readonly client: AdminApiClient }) {
  const load = () => client.commissions.get();
  const [state, retry] = useData(load, 'commissions');
  const [actionError, setActionError] = useState<unknown>(null);
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <Panel title="Commission settings" state={state} onRetry={retry}>
        {state.status === 'loaded' ? (
          <>
            <dl className="admin-definition-list">
              <dt>Default commission basis points</dt>
              <dd>{field(state.value, 'defaultCommissionBps')}</dd>
            </dl>
            <DataTable
              caption="Retailer commission overrides"
              rows={
                Array.isArray(state.value.retailerOverrides)
                  ? (state.value.retailerOverrides as readonly AdminRecord[])
                  : []
              }
              rowKey={(row) => field(row, 'retailerId')}
              emptyMessage="No retailer commission overrides are set."
              columns={[
                { key: 'retailer', header: 'Retailer', render: (row) => field(row, 'retailerId') },
                {
                  key: 'rate',
                  header: 'Commission basis points',
                  numeric: true,
                  render: (row) => field(row, 'commissionRateBps'),
                },
              ]}
            />
          </>
        ) : null}
      </Panel>
      {actionError === null ? null : actionFailure(actionError)}
      <form
        className="admin-inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const reason = String(form.get('reason') ?? '').trim();
          const retailerId = String(form.get('retailerId') ?? '').trim();
          const defaultCommissionBps = optionalNumber(form.get('defaultCommissionBps'));
          const commissionRateBps = optionalNumber(form.get('commissionRateBps'));
          const clearOverride = form.get('clearOverride') === 'on';
          if (
            reason === '' ||
            (defaultCommissionBps === undefined &&
              (retailerId === '' || (commissionRateBps === undefined && !clearOverride)))
          )
            return;
          const input: Record<string, unknown> = { reason };
          if (defaultCommissionBps !== undefined) input.defaultCommissionBps = defaultCommissionBps;
          if (retailerId !== '') input.retailerId = retailerId;
          if (commissionRateBps !== undefined) input.commissionRateBps = commissionRateBps;
          if (clearOverride) input.commissionRateBps = null;
          setActionError(null);
          void client.commissions.update(input).then(retry, setActionError);
        }}
      >
        <h2>Change a persisted commission setting</h2>
        <Field
          id="default-commission-bps"
          name="defaultCommissionBps"
          label="Default commission basis points"
          inputMode="numeric"
        />
        <Field id="commission-retailer-id" name="retailerId" label="Retailer ID for an override" />
        <Field
          id="commission-rate-bps"
          name="commissionRateBps"
          label="Retailer commission basis points"
          inputMode="numeric"
        />
        <label>
          <input name="clearOverride" type="checkbox" /> Clear retailer override
        </label>
        <Field id="commission-reason" name="reason" label="Reason" required />
        <Button type="submit" style={{ minWidth: '44px' }}>
          Save commission setting
        </Button>
      </form>
    </section>
  );
}

function MarketStatusSelect({
  id,
  name,
  defaultValue,
  label,
}: {
  readonly id: string;
  readonly name: string;
  readonly defaultValue: MarketStatus;
  readonly label: string;
}) {
  return (
    <label htmlFor={id}>
      {label}
      <select id={id} name={name} defaultValue={defaultValue}>
        {MARKET_STATUSES.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </select>
    </label>
  );
}

function MarketDetails({
  market,
  client,
  refresh,
}: {
  readonly market: AdminMarket;
  readonly client: AdminApiClient;
  readonly refresh: () => void;
}) {
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const selectedArea =
    market.service_areas.find((area) => area.id === selectedAreaId) ?? market.service_areas[0];

  const updateMarket = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const status = String(form.get('status') ?? '');
    const reason = String(form.get('reason') ?? '').trim();
    if (!isMarketStatus(status) || reason === '') return;
    setActionError(null);
    void client.markets.update(market.id, { status, reason }).then(refresh, setActionError);
  };

  const createServiceArea = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const status = String(form.get('status') ?? '');
    const slug = String(form.get('slug') ?? '').trim();
    const name = String(form.get('name') ?? '').trim();
    const reason = String(form.get('reason') ?? '').trim();
    const priority = optionalNumber(form.get('priority'));
    if (
      !isMarketStatus(status) ||
      slug === '' ||
      name === '' ||
      reason === '' ||
      priority === undefined
    )
      return;
    setActionError(null);
    void client.markets
      .createServiceArea(market.id, {
        slug,
        name,
        status,
        deliveryEnabled: form.get('deliveryEnabled') === 'on',
        pickupEnabled: form.get('pickupEnabled') === 'on',
        overnightEnabled: form.get('overnightEnabled') === 'on',
        priority,
        reason,
      })
      .then(refresh, setActionError);
  };

  const updateServiceArea = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedArea === undefined) return;
    const form = new FormData(event.currentTarget);
    const status = String(form.get('status') ?? '');
    const reason = String(form.get('reason') ?? '').trim();
    const priority = optionalNumber(form.get('priority'));
    if (!isMarketStatus(status) || reason === '' || priority === undefined) return;
    setActionError(null);
    void client.markets
      .updateServiceArea(selectedArea.id, {
        status,
        deliveryEnabled: form.get('deliveryEnabled') === 'on',
        pickupEnabled: form.get('pickupEnabled') === 'on',
        overnightEnabled: form.get('overnightEnabled') === 'on',
        priority,
        reason,
      })
      .then(refresh, setActionError);
  };

  return (
    <section aria-labelledby="market-inspection-heading">
      <h2 id="market-inspection-heading">Inspect {market.name}</h2>
      <dl className="admin-definition-list">
        <dt>Market status</dt>
        <dd>{market.status}</dd>
        <dt>Time zone</dt>
        <dd>{market.timezone}</dd>
        <dt>Region</dt>
        <dd>{market.region ?? '—'}</dd>
      </dl>
      <p className="admin-market-consequence">
        A LIVE market is selectable during customer bootstrap. Customer discovery and After Hours
        also require a live market plus their other server-side eligibility checks. Pausing or
        retiring a market removes its live-market eligibility.
      </p>
      {actionError === null ? null : actionFailure(actionError)}
      <form className="admin-inline-form" onSubmit={(event) => void updateMarket(event)}>
        <h3>Change market status</h3>
        <MarketStatusSelect
          id={`market-status-${market.id}`}
          name="status"
          defaultValue={market.status}
          label="Status"
        />
        <Field id={`market-reason-${market.id}`} name="reason" label="Reason" required />
        <Button type="submit" style={{ minWidth: '44px', minHeight: '44px' }}>
          Save market status
        </Button>
      </form>
      <section aria-labelledby="service-areas-heading">
        <h3 id="service-areas-heading">Service areas</h3>
        {market.service_areas.length === 0 ? (
          <EmptyState
            title="No service areas"
            description="Create a service area when this market is ready to define coverage."
          />
        ) : (
          <DataTable
            caption={`Service areas in ${market.name}`}
            rows={market.service_areas}
            rowKey={(area) => area.id}
            columns={[
              { key: 'name', header: 'Service area', render: (area) => area.name },
              { key: 'status', header: 'Status', render: (area) => area.status },
              {
                key: 'delivery',
                header: 'Delivery',
                render: (area) => (area.deliveryEnabled ? 'Enabled' : 'Not enabled'),
              },
              {
                key: 'pickup',
                header: 'Pickup',
                render: (area) => (area.pickupEnabled ? 'Enabled' : 'Not enabled'),
              },
              {
                key: 'select',
                header: 'Inspect',
                render: (area) => (
                  <Button
                    type="button"
                    aria-label={`Inspect service area ${area.name}`}
                    style={{ minWidth: '44px', minHeight: '44px' }}
                    onClick={() => setSelectedAreaId(area.id)}
                  >
                    Inspect
                  </Button>
                ),
              },
            ]}
          />
        )}
        {selectedArea === undefined ? null : (
          <form className="admin-inline-form" onSubmit={(event) => void updateServiceArea(event)}>
            <h4>Change {selectedArea.name}</h4>
            <MarketStatusSelect
              id={`service-area-status-${selectedArea.id}`}
              name="status"
              defaultValue={selectedArea.status}
              label="Status"
            />
            <Field
              id={`service-area-priority-${selectedArea.id}`}
              name="priority"
              label="Priority"
              inputMode="numeric"
              defaultValue={String(selectedArea.priority)}
              required
            />
            <label>
              <input
                name="deliveryEnabled"
                type="checkbox"
                defaultChecked={selectedArea.deliveryEnabled}
              />{' '}
              Delivery enabled
            </label>
            <label>
              <input
                name="pickupEnabled"
                type="checkbox"
                defaultChecked={selectedArea.pickupEnabled}
              />{' '}
              Pickup enabled
            </label>
            <label>
              <input
                name="overnightEnabled"
                type="checkbox"
                defaultChecked={selectedArea.overnightEnabled}
              />{' '}
              Overnight enabled
            </label>
            <Field
              id={`service-area-reason-${selectedArea.id}`}
              name="reason"
              label="Reason"
              required
            />
            <Button type="submit" style={{ minWidth: '44px', minHeight: '44px' }}>
              Save service area
            </Button>
          </form>
        )}
      </section>
      <form className="admin-inline-form" onSubmit={(event) => void createServiceArea(event)}>
        <h3>Add a service area</h3>
        <Field id={`new-area-slug-${market.id}`} name="slug" label="Slug" required />
        <Field id={`new-area-name-${market.id}`} name="name" label="Name" required />
        <MarketStatusSelect
          id={`new-area-status-${market.id}`}
          name="status"
          defaultValue="PLANNED"
          label="Status"
        />
        <Field
          id={`new-area-priority-${market.id}`}
          name="priority"
          label="Priority"
          inputMode="numeric"
          defaultValue="0"
          required
        />
        <label>
          <input name="deliveryEnabled" type="checkbox" /> Delivery enabled
        </label>
        <label>
          <input name="pickupEnabled" type="checkbox" /> Pickup enabled
        </label>
        <label>
          <input name="overnightEnabled" type="checkbox" /> Overnight enabled
        </label>
        <Field id={`new-area-reason-${market.id}`} name="reason" label="Reason" required />
        <Button type="submit" style={{ minWidth: '44px', minHeight: '44px' }}>
          Add service area
        </Button>
      </form>
    </section>
  );
}

function Markets({ client }: { readonly client: AdminApiClient }) {
  const load = () => client.markets.list();
  const [state, retry] = useData(load, 'markets');
  const [selectedMarketId, setSelectedMarketId] = useState<string | null>(null);
  const selected =
    state.status === 'loaded'
      ? (state.value.find((market) => market.id === selectedMarketId) ?? state.value[0])
      : undefined;
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <Panel
        title="Markets"
        state={state}
        onRetry={retry}
        emptyTitle={
          state.status === 'loaded' && state.value.length === 0
            ? 'No markets configured'
            : undefined
        }
        emptyDescription="Create a market through the approved administrator API before defining service areas."
      >
        {state.status === 'loaded' ? (
          <DataTable
            caption="Configured markets"
            rows={state.value}
            rowKey={(market) => market.id}
            columns={[
              { key: 'name', header: 'Market', render: (market) => market.name },
              { key: 'status', header: 'Status', render: (market) => market.status },
              { key: 'timezone', header: 'Time zone', render: (market) => market.timezone },
              {
                key: 'inspect',
                header: 'Inspect',
                render: (market) => (
                  <Button
                    type="button"
                    aria-label={`Inspect market ${market.name}`}
                    style={{ minWidth: '44px', minHeight: '44px' }}
                    onClick={() => setSelectedMarketId(market.id)}
                  >
                    Inspect
                  </Button>
                ),
              },
            ]}
          />
        ) : null}
      </Panel>
      {selected === undefined ? null : (
        <MarketDetails market={selected} client={client} refresh={retry} />
      )}
    </section>
  );
}

/**
 * Older embedded administrator hosts did not inject the optional Phase 3
 * capabilities. Their existing overview call preserves an honest loading/error
 * state while displaying an empty report rather than fabricated lead data.
 */
function legacyEmpty<T>(client: AdminApiClient, value: T): Promise<T> {
  return client.overview.get().then(() => value);
}

function Leads({ client }: { readonly client: AdminApiClient }) {
  const load = () =>
    client.leads?.list() ??
    legacyEmpty(client, {
      customer: [] as readonly AdminLead[],
      retailer: [] as readonly AdminLead[],
    });
  const [state, retry] = useData(load, 'leads');
  const [updateError, setUpdateError] = useState<unknown>(null);
  const rows: readonly LeadRow[] =
    state.status !== 'loaded'
      ? []
      : [
          ...state.value.customer.map((lead) => ({ ...lead, kind: 'CUSTOMER' as const })),
          ...state.value.retailer.map((lead) => ({ ...lead, kind: 'RETAILER' as const })),
        ];

  const updateStatus = (lead: LeadRow, status: string) => {
    if (!isLeadStatus(status) || client.leads === undefined) return;
    setUpdateError(null);
    void client.leads
      .updateStatus(lead.id, { kind: lead.kind, status })
      .then(retry)
      .catch((error: unknown) => setUpdateError(error));
  };

  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <p>
        Review lead status deliberately. Changing a retailer lead does not approve, onboard, or
        activate a retailer.
      </p>
      {updateError === null ? null : actionFailure(updateError)}
      <Panel
        title="Leads"
        state={state}
        onRetry={retry}
        emptyTitle={state.status === 'loaded' && rows.length === 0 ? 'No data yet' : undefined}
        emptyDescription="No customer or retailer leads are awaiting review."
      >
        {state.status === 'loaded' ? (
          <DataTable
            caption="Customer and retailer lead review queue"
            rows={rows}
            rowKey={(lead) => `${lead.kind}-${lead.id}`}
            columns={[
              { key: 'kind', header: 'Type', render: (lead) => lead.kind },
              { key: 'lead', header: 'Lead', render: (lead) => field(lead, 'email') },
              { key: 'detail', header: 'Detail', render: (lead) => field(lead, 'business_name') },
              { key: 'status', header: 'Status', render: (lead) => lead.status },
              {
                key: 'review',
                header: 'Update review status',
                render: (lead) => (
                  <select
                    aria-label={`Review status for ${lead.kind.toLowerCase()} lead ${lead.id}`}
                    defaultValue={isLeadStatus(lead.status) ? lead.status : 'NEW'}
                    onChange={(event) => updateStatus(lead, event.currentTarget.value)}
                    style={{ minWidth: '44px', minHeight: '44px' }}
                  >
                    {LEAD_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                ),
              },
            ]}
          />
        ) : null}
      </Panel>
    </section>
  );
}

function Acquisition({ client }: { readonly client: AdminApiClient }) {
  const load = () => client.insights?.acquisition() ?? legacyEmpty(client, { sources: [] });
  const [state, retry] = useData(load, 'acquisition');
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <p>
        Aggregate first-touch attribution only. This report does not expose individual visitors.
      </p>
      <Panel
        title="Acquisition report"
        state={state}
        onRetry={retry}
        emptyTitle={
          state.status === 'loaded' && state.value.sources.length === 0 ? 'No data yet' : undefined
        }
        emptyDescription="No first-party attribution touches have been recorded."
      >
        {state.status === 'loaded' ? (
          <DataTable
            caption="Acquisition sources"
            rows={state.value.sources}
            rowKey={(source: AcquisitionSource) =>
              `${source.source}-${source.medium}-${source.campaign}`
            }
            columns={[
              { key: 'source', header: 'Source', render: (source) => source.source },
              { key: 'medium', header: 'Medium', render: (source) => source.medium },
              { key: 'campaign', header: 'Campaign', render: (source) => source.campaign },
              { key: 'visitors', header: 'Visitors', render: (source) => source.visitors },
              {
                key: 'orders',
                header: 'Attributed orders',
                render: (source) => source.attributedOrders,
              },
            ]}
          />
        ) : null}
      </Panel>
    </section>
  );
}

function SearchTermInsights({ client }: { readonly client: AdminApiClient }) {
  const load = () =>
    client.insights?.searchTerms() ??
    legacyEmpty(client, { topTerms: [] as readonly SearchTermInsight[], zeroResultTerms: [] });
  const [state, retry] = useData(load, 'search-terms');
  const empty =
    state.status === 'loaded' &&
    state.value.topTerms.length === 0 &&
    state.value.zeroResultTerms.length === 0;
  const columns = [
    { key: 'term', header: 'Normalized term', render: (term: SearchTermInsight) => term.term },
    {
      key: 'market',
      header: 'Market',
      render: (term: SearchTermInsight) => term.marketId ?? 'All markets',
    },
    { key: 'searches', header: 'Searches', render: (term: SearchTermInsight) => term.searchCount },
    {
      key: 'zero-results',
      header: 'Zero-result searches',
      render: (term: SearchTermInsight) => term.zeroResultCount,
    },
  ];
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <p>
        Demand summaries use normalized terms and aggregate counts; they do not show individual
        activity.
      </p>
      <Panel
        title="Search term insights"
        state={state}
        onRetry={retry}
        emptyTitle={empty ? 'No data yet' : undefined}
        emptyDescription="No normalized search-term statistics have been recorded."
      >
        {state.status === 'loaded' ? (
          <>
            <h2>Top search terms</h2>
            <DataTable
              caption="Top normalized search terms"
              rows={state.value.topTerms}
              rowKey={(term) => `${term.marketId ?? 'all'}-${term.term}`}
              columns={columns}
            />
            <h2>Zero-result demand</h2>
            <DataTable
              caption="Normalized terms with zero-result searches"
              rows={state.value.zeroResultTerms}
              rowKey={(term) => `zero-${term.marketId ?? 'all'}-${term.term}`}
              columns={columns}
            />
          </>
        ) : null}
      </Panel>
    </section>
  );
}

function commaSeparatedIds(value: FormDataEntryValue | null): readonly string[] {
  if (typeof value !== 'string') return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function requiredInteger(value: FormDataEntryValue | null): number | undefined {
  const parsed = optionalNumber(value);
  return parsed !== undefined && Number.isInteger(parsed) ? parsed : undefined;
}

function optionalIntegerOrNull(value: FormDataEntryValue | null): number | null | undefined {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = requiredInteger(value);
  return parsed;
}

interface PlanCatalogueRow {
  readonly plan: AdminMembershipPlan;
  readonly version: AdminMembershipPlanVersion | null;
}

function membershipDate(value: unknown): string {
  return timestampText(value);
}

function Memberships({
  client,
  flags = {},
}: {
  readonly client: AdminApiClient;
  readonly flags?: Readonly<Record<string, boolean>>;
}) {
  const load = () => client.membership.listMemberships();
  const [membershipState, reloadMemberships] = useData(load, 'memberships');
  const loadPlanCatalogue = () => client.membership.listPlans();
  const [planCatalogueState, reloadPlanCatalogue] = useData(loadPlanCatalogue, 'membership-plans');
  const [createdPlan, setCreatedPlan] = useState<
    (MembershipPlanCreateInput & { readonly versionId: string | null }) | null
  >(null);
  const [planError, setPlanError] = useState<unknown>(null);
  const [ruleError, setRuleError] = useState<unknown>(null);
  const [ruleCreated, setRuleCreated] = useState<string | null>(null);
  const [selectedMembership, setSelectedMembership] = useState<AdminMembership | null>(null);
  const [attemptState, setAttemptState] = useState<Remote<
    readonly AdminMembershipBillingAttempt[]
  > | null>(null);
  const sandboxOnly = flags['membership.recurring_billing'] !== true;

  const inspectAttempts = (membership: AdminMembership) => {
    setSelectedMembership(membership);
    setAttemptState({ status: 'loading' });
    void client.membership.billingAttempts(membership.id).then(
      (value) => setAttemptState({ status: 'loaded', value }),
      (error: unknown) => setAttemptState({ status: 'failed', error }),
    );
  };

  const createPlan = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const priceMinor = requiredInteger(form.get('priceMinor'));
    const input: MembershipPlanCreateInput = {
      code: String(form.get('code') ?? '').trim(),
      name: String(form.get('name') ?? '').trim(),
      priceMinor: priceMinor ?? -1,
      currency: String(form.get('currency') ?? '')
        .trim()
        .toUpperCase(),
    };
    if (input.code === '' || input.name === '' || input.priceMinor < 0 || input.currency === '')
      return;
    setPlanError(null);
    try {
      const created = await client.membership.createPlan(input);
      setCreatedPlan({
        ...input,
        versionId: typeof created.versionId === 'string' ? created.versionId : null,
      });
      event.currentTarget.reset();
    } catch (error) {
      setPlanError(error);
    }
  };

  const createBenefitRule = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const minimumOrderMinor = requiredInteger(form.get('minimumOrderMinor'));
    const feeReductionMinor = requiredInteger(form.get('feeReductionMinor'));
    const maximumDistanceMeters = optionalIntegerOrNull(form.get('maximumDistanceMeters'));
    const startMinute = optionalIntegerOrNull(form.get('startMinute'));
    const endMinute = optionalIntegerOrNull(form.get('endMinute'));
    const usageLimitPerPeriod = optionalIntegerOrNull(form.get('usageLimitPerPeriod'));
    if (
      minimumOrderMinor === undefined ||
      feeReductionMinor === undefined ||
      maximumDistanceMeters === undefined ||
      startMinute === undefined ||
      endMinute === undefined ||
      usageLimitPerPeriod === undefined
    )
      return;
    const deliveryType = String(form.get('deliveryType') ?? '');
    if (!['STANDARD', 'EXPRESS', 'OVERNIGHT'].includes(deliveryType)) return;
    const input: MembershipBenefitRuleCreateInput = {
      planVersionId: String(form.get('planVersionId') ?? '').trim(),
      minimumOrderMinor,
      maximumDistanceMeters,
      deliveryType: deliveryType as MembershipBenefitRuleCreateInput['deliveryType'],
      startMinute,
      endMinute,
      usageLimitPerPeriod,
      waiveBaseFee: form.get('waiveBaseFee') === 'on',
      feeReductionMinor,
      marketIds: commaSeparatedIds(form.get('marketIds')),
      sellerIds: commaSeparatedIds(form.get('sellerIds')),
    };
    if (input.planVersionId === '') return;
    setRuleError(null);
    setRuleCreated(null);
    try {
      const created = await client.membership.createBenefitRule(input);
      setRuleCreated(typeof created.id === 'string' ? created.id : 'created');
      event.currentTarget.reset();
    } catch (error) {
      setRuleError(error);
    }
  };

  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      {sandboxOnly ? (
        <Card>
          <h2>Sandbox billing only</h2>
          <p>
            Recurring billing is off. Enrollment and renewals use sandbox billing and no real money
            moves. UI BUILT — INTEGRATION ACCOUNT REQUIRED.
          </p>
        </Card>
      ) : null}
      <Card>
        <h2>Create a membership plan</h2>
        <p>
          Prices are integer minor units in the plan currency. The server creates version 1 for each
          new plan.
        </p>
        {planError === null ? null : actionFailure(planError)}
        <form className="admin-inline-form" onSubmit={(event) => void createPlan(event)}>
          <Field id="membership-plan-code" name="code" label="Plan code" required />
          <Field id="membership-plan-name" name="name" label="Plan name" required />
          <Field
            id="membership-plan-price-minor"
            name="priceMinor"
            label="Recurring price (minor units, in plan currency)"
            inputMode="numeric"
            required
          />
          <Field
            id="membership-plan-currency"
            name="currency"
            label="Currency (three-letter code)"
            defaultValue="USD"
            required
          />
          <Button type="submit" style={{ minWidth: '44px', minHeight: '44px' }}>
            Create plan
          </Button>
        </form>
        {createdPlan === null ? null : (
          <dl className="admin-definition-list" aria-label="Created membership plan">
            <dt>Created plan</dt>
            <dd>{createdPlan.name}</dd>
            <dt>Code</dt>
            <dd>{createdPlan.code}</dd>
            <dt>Version</dt>
            <dd>
              {createdPlan.versionId ?? 'Created; server did not return a version identifier.'}
            </dd>
            <dt>Recurring price</dt>
            <dd>{money(createdPlan.priceMinor, createdPlan.currency)}</dd>
          </dl>
        )}
      </Card>
      <section aria-labelledby="membership-plan-catalogue-heading">
        <h2 id="membership-plan-catalogue-heading">Plan catalogue</h2>
        <Panel
          title="Beauty Burst+ plans"
          state={planCatalogueState}
          onRetry={reloadPlanCatalogue}
          emptyTitle={
            planCatalogueState.status === 'loaded' && planCatalogueState.value.length === 0
              ? 'No plans'
              : undefined
          }
          emptyDescription="No Beauty Burst+ plans have been created yet."
        >
          {planCatalogueState.status === 'loaded' ? (
            <DataTable
              caption="Beauty Burst+ plans and versions returned by the administrator API"
              rows={planCatalogueState.value.flatMap<PlanCatalogueRow>((plan) =>
                plan.versions.length === 0
                  ? [{ plan, version: null }]
                  : plan.versions.map((version) => ({ plan, version })),
              )}
              rowKey={(row) => `${row.plan.id}:${row.version?.id ?? 'none'}`}
              columns={[
                { key: 'name', header: 'Plan', render: (row) => row.plan.name },
                { key: 'code', header: 'Code', render: (row) => row.plan.code },
                { key: 'status', header: 'Status', render: (row) => row.plan.status },
                {
                  key: 'version',
                  header: 'Version',
                  render: (row) =>
                    row.version === null ? 'No version recorded' : String(row.version.version),
                },
                {
                  key: 'versionId',
                  header: 'Version ID (use in benefit rules)',
                  render: (row) => row.version?.id ?? '—',
                },
                {
                  key: 'price',
                  header: 'Recurring price',
                  render: (row) =>
                    row.version === null
                      ? '—'
                      : money(row.version.price_minor, row.version.currency),
                },
                {
                  key: 'retired',
                  header: 'Retired',
                  render: (row) =>
                    row.version === null
                      ? '—'
                      : row.version.retired_at === null
                        ? 'Current'
                        : membershipDate(row.version.retired_at),
                },
              ]}
            />
          ) : null}
        </Panel>
      </section>
      <Card>
        <h2>Configure a delivery benefit rule</h2>
        <p>
          Leave an optional condition blank to make it unlimited: maximum distance, hours window,
          and uses per period. Empty market or seller lists mean the rule has no participating
          market or seller restriction recorded by this form.
        </p>
        <p>
          If this rule waives the base fee, express or overnight surcharges can still apply. Do not
          advertise this benefit as free delivery.
        </p>
        {ruleError === null ? null : actionFailure(ruleError)}
        {ruleCreated === null ? null : <p>Benefit rule {ruleCreated} was created.</p>}
        <form className="admin-inline-form" onSubmit={(event) => void createBenefitRule(event)}>
          <Field
            id="benefit-plan-version-id"
            name="planVersionId"
            label="Plan version ID"
            required
          />
          <Field
            id="benefit-minimum-order-minor"
            name="minimumOrderMinor"
            label="Minimum order amount (minor units)"
            inputMode="numeric"
            defaultValue="0"
            required
          />
          <Field
            id="benefit-maximum-distance"
            name="maximumDistanceMeters"
            label="Maximum distance (meters; blank for unlimited)"
            inputMode="numeric"
          />
          <label htmlFor="benefit-delivery-type">
            Delivery type
            <select id="benefit-delivery-type" name="deliveryType" defaultValue="STANDARD">
              <option value="STANDARD">Standard</option>
              <option value="EXPRESS">Express</option>
              <option value="OVERNIGHT">Overnight</option>
            </select>
          </label>
          <Field
            id="benefit-start-minute"
            name="startMinute"
            label="Hours window start (minute after midnight; blank for unlimited)"
            inputMode="numeric"
          />
          <Field
            id="benefit-end-minute"
            name="endMinute"
            label="Hours window end (minute after midnight; blank for unlimited)"
            inputMode="numeric"
          />
          <Field
            id="benefit-usage-limit"
            name="usageLimitPerPeriod"
            label="Usage limit per period (blank for unlimited)"
            inputMode="numeric"
          />
          <label>
            <input name="waiveBaseFee" type="checkbox" defaultChecked /> Waive base delivery fee
          </label>
          <Field
            id="benefit-fee-reduction-minor"
            name="feeReductionMinor"
            label="Fee reduction amount (minor units)"
            inputMode="numeric"
            defaultValue="0"
            required
          />
          <Field
            id="benefit-market-ids"
            name="marketIds"
            label="Participating market IDs"
            hint="Separate IDs with commas. Leave blank for no market restriction."
          />
          <Field
            id="benefit-seller-ids"
            name="sellerIds"
            label="Participating seller IDs"
            hint="Separate IDs with commas. Leave blank for no seller restriction."
          />
          <Button type="submit" style={{ minWidth: '44px', minHeight: '44px' }}>
            Create delivery benefit rule
          </Button>
        </form>
      </Card>
      <section aria-labelledby="membership-supervision-heading">
        <h2 id="membership-supervision-heading">Member supervision</h2>
        <Panel
          title="Memberships"
          state={membershipState}
          onRetry={reloadMemberships}
          emptyTitle={
            membershipState.status === 'loaded' && membershipState.value.length === 0
              ? 'No memberships'
              : undefined
          }
          emptyDescription="No memberships have been returned by the administrator API."
        >
          {membershipState.status === 'loaded' ? (
            <DataTable
              caption="Memberships returned by the administrator API"
              rows={membershipState.value}
              rowKey={(membership) => membership.id}
              columns={[
                { key: 'id', header: 'Membership', render: (membership) => membership.id },
                { key: 'status', header: 'Status', render: (membership) => membership.status },
                {
                  key: 'plan',
                  header: 'Plan and version',
                  render: (membership) => `${membership.plan_id} / ${membership.plan_version_id}`,
                },
                {
                  key: 'period',
                  header: 'Current period',
                  render: (membership) =>
                    `${membershipDate(membership.current_period_start)} to ${membershipDate(membership.current_period_end)}`,
                },
                {
                  key: 'renewal',
                  header: 'Renewal',
                  render: (membership) =>
                    membership.cancel_at_period_end
                      ? 'Non-renewing at period end'
                      : membership.renewal_enabled
                        ? 'Renewing'
                        : 'Not renewing',
                },
                {
                  key: 'attempts',
                  header: 'Billing attempts',
                  render: (membership) => (
                    <Button
                      type="button"
                      aria-label={`Inspect billing attempts for membership ${membership.id}`}
                      style={{ minWidth: '44px', minHeight: '44px' }}
                      onClick={() => inspectAttempts(membership)}
                    >
                      Inspect
                    </Button>
                  ),
                },
              ]}
            />
          ) : null}
        </Panel>
        {selectedMembership === null || attemptState === null ? null : (
          <section aria-labelledby="billing-attempts-heading">
            <h3 id="billing-attempts-heading">
              Billing attempts for membership {selectedMembership.id}
            </h3>
            <Panel
              title="Billing attempts"
              state={attemptState}
              onRetry={() => inspectAttempts(selectedMembership)}
              emptyTitle={
                attemptState.status === 'loaded' && attemptState.value.length === 0
                  ? 'No billing attempts'
                  : undefined
              }
              emptyDescription="This membership has no billing attempts recorded."
            >
              {attemptState.status === 'loaded' ? (
                <DataTable
                  caption={`Billing attempts for membership ${selectedMembership.id}`}
                  rows={attemptState.value}
                  rowKey={(attempt) => attempt.id}
                  columns={[
                    {
                      key: 'attempt',
                      header: 'Attempt',
                      render: (attempt) => attempt.attempt_number,
                    },
                    { key: 'kind', header: 'Kind', render: (attempt) => attempt.kind },
                    { key: 'outcome', header: 'Outcome', render: (attempt) => attempt.outcome },
                    {
                      key: 'amount',
                      header: 'Amount',
                      numeric: true,
                      render: (attempt) => money(attempt.amount_minor, attempt.currency),
                    },
                    {
                      key: 'failure',
                      header: 'Failure detail',
                      render: (attempt) => attempt.failure_reason ?? '—',
                    },
                    {
                      key: 'time',
                      header: 'Attempted',
                      render: (attempt) => membershipDate(attempt.attempted_at),
                    },
                  ]}
                />
              ) : null}
            </Panel>
          </section>
        )}
      </section>
    </section>
  );
}

export function LegacyAdminScreen({
  route,
  client,
  flags,
}: {
  readonly route: RouteContract;
  readonly client: AdminApiClient;
  readonly flags?: Readonly<Record<string, boolean>>;
}) {
  switch (route.path) {
    case '/':
      return <Overview client={client} />;
    case '/retailers':
      return <Retailers client={client} />;
    case '/users':
      return <Users client={client} />;
    case '/feature-flags':
      return <FeatureFlags client={client} />;
    case '/audit-log':
      return <Audit client={client} />;
    case '/leads':
      return <Leads client={client} />;
    case '/acquisition':
      return <Acquisition client={client} />;
    case '/search-terms':
      return <SearchTermInsights client={client} />;
    case '/memberships':
      return <Memberships client={client} flags={flags} />;
    case '/orders':
      return <Orders client={client} />;
    case '/refunds':
      return <OperationalExceptions client={client} />;
    case '/payouts':
      return <Payouts client={client} />;
    case '/commissions':
      return <Commissions client={client} />;
    case '/markets':
      return <Markets client={client} />;
    default:
      return null;
  }
}
